import classNames from 'classnames'
import { Link } from 'gatsby'
import type {
  SegmentationCardData,
  SegmentationCarouselTheme,
} from 'src/types/segmentationCarousel'

interface SegmentationCardProps {
  card: SegmentationCardData
  theme: SegmentationCarouselTheme
}

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 8h9M8.5 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function SegmentationCard({
  card,
  theme,
}: SegmentationCardProps) {
  const {
    textPrimary,
    textSecondary,
    textTertiary,
    link,
    backgroundColor,
    textColor,
  } = card

  const isInteractive = Boolean(link?.trim())

  const accessibleName = [textPrimary, textSecondary, textTertiary]
    .filter(Boolean)
    .join(' — ')

  const cardClassName = classNames(
    'group flex h-full w-full flex-col justify-start gap-3 p-5 md:justify-between md:gap-6 md:min-h-[210px]',
    isInteractive &&
      'transition-transform duration-200 ease-out hover:-translate-y-[5px]',
    isInteractive &&
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    isInteractive &&
      (theme === 'dark'
        ? 'focus-visible:ring-white focus-visible:ring-offset-[#0F0F0F]'
        : 'focus-visible:ring-fullDark focus-visible:ring-offset-white')
  )

  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        {textPrimary && (
          <span className="text-3xl font-bold leading-none opacity-80 md:text-[40px]">
            {textPrimary}
          </span>
        )}
        {isInteractive && (
          <span
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-fullDark transition-transform duration-200 ease-out group-hover:translate-x-1 md:h-12 md:w-12"
          >
            <ArrowIcon />
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        {textSecondary && (
          <span className="text-sm font-bold md:text-base">
            {textSecondary}
          </span>
        )}
        {textTertiary && (
          <span className="text-xs font-normal md:text-sm min-h-[33.58px]">
            {textTertiary}
          </span>
        )}
      </div>
    </>
  )

  const style = { backgroundColor, color: textColor }

  if (link?.trim()) {
    return (
      <Link
        to={link}
        data-testid="segmentation-card"
        aria-label={accessibleName || undefined}
        className={cardClassName}
        style={style}
      >
        {content}
      </Link>
    )
  }

  return (
    <div
      data-testid="segmentation-card"
      className={cardClassName}
      style={style}
    >
      {content}
    </div>
  )
}
